import { Controller } from "@hotwired/stimulus"
import db from "../field_db"
import { warmUrlsForSync } from "../field_offline_resources"
import { observedFetch, currentLevel } from "../field_network_health"
import { timeoutFor } from "../field_timeout_policy"
import { logEvent, logError } from "../field_logger"
import { upsertCachedPlace, upsertCachedResponse } from "../field_sync_shared"

// Downloads the Tasker's upcoming surveys from the server and stores them in
// IndexedDB for offline access. Exposes a "Sync for offline" button with
// last-synced timestamp and progress feedback.
//
// Auto-triggers a sync when the user switches to Manual offline mode (so the
// data is ready before they go offline).
//
// Usage:
//   data-controller="field-prefetch"
//   data-field-prefetch-target="button"     -- the sync button
//   data-field-prefetch-target="status"     -- status / last-synced label
//   data-field-prefetch-target="spinner"    -- loading indicator (hidden by default)

export default class extends Controller {
  static targets = ["button", "status", "spinner"]

  connect() {
    this._syncing = false
    this._updateStatus()

    // Auto-trigger when the user switches to Manual offline mode
    this._modeChangedHandler = (e) => {
      if (e.detail?.mode === "manual") this.sync()
    }
    window.addEventListener("field:offline-mode-changed", this._modeChangedHandler)
  }

  disconnect() {
    window.removeEventListener("field:offline-mode-changed", this._modeChangedHandler)
  }

  async sync() {
    if (this._syncing) return

    this._syncing = true
    this._setLoading(true)
    logEvent("sync", "Manual prefetch sync started").catch(() => {})

    try {
      const csrfToken = document.querySelector("[name='csrf-token']")?.content
      const response = await observedFetch("/api/v1/field/prefetch.json", {
        method: "GET",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken
        },
        signal: AbortSignal.timeout(timeoutFor("prefetch", currentLevel()))
      }, { kind: "prefetch", sizeClass: "small" })

      if (!response.ok) throw new Error(`Server error: ${response.status}`)

      const { responses, cached_at } = await response.json()

      // Upsert each place into cached_places (deduplicated by placeId)
      const seenPlaceIds = new Set()
      for (const r of responses) {
        if (!r.place?.id || seenPlaceIds.has(r.place.id)) continue
        seenPlaceIds.add(r.place.id)
        await upsertCachedPlace(db, r.place)
      }

      // Upsert each response into cached_responses. Shared helper (card
      // #3804) merges any existing local draft over the server's resultJson
      // instead of overwriting it outright -- a manual "Sync for offline"
      // tap must not clobber in-progress answers in an open editor tab.
      for (const r of responses) {
        await upsertCachedResponse(db, r, cached_at)
      }

      // Remove cached responses that are no longer in the prefetch payload
      // (ended or no longer assigned to this Tasker)
      const prefetchIds = new Set(responses.map(r => r.id))
      await db.cached_responses
        .filter(r => !prefetchIds.has(r.responseId))
        .delete()

      localStorage.setItem("field:last-prefetch", cached_at)
      this._updateStatus()

      // Warm shell pages and per-resource URLs into the SW page cache.
      // What gets warmed is declared in field_offline_resources.js.
      const sw = navigator.serviceWorker?.controller
      if (sw) sw.postMessage({ type: "WARM_CACHE", urls: warmUrlsForSync(responses) })

      window.dispatchEvent(new CustomEvent("field:prefetch-complete", {
        detail: { count: responses.length }
      }))
      logEvent("sync", "Manual prefetch sync complete", { count: responses.length }).catch(() => {})
    } catch (error) {
      console.error("Prefetch failed:", error)
      // Manual, user-triggered sync with no automatic retry -- terminal for
      // this attempt (semantics table, docs/dev_patterns/pwa-diagnostics-telemetry.md).
      logError("sync", "Manual prefetch sync failed", { message: error?.message || String(error) }).catch(() => {})
      this._setStatusText("Sync failed — try again")
    } finally {
      this._syncing = false
      this._setLoading(false)
    }
  }

  // -- private --

  _updateStatus() {
    const lastSync = localStorage.getItem("field:last-prefetch")
    if (lastSync) {
      const date = new Date(lastSync)
      const formatted = date.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })
      this._setStatusText(`Last synced ${formatted}`)
    } else {
      this._setStatusText("Not yet synced")
    }
  }

  _setStatusText(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text
  }

  _setLoading(loading) {
    if (this.hasSpinnerTarget) {
      if (loading) {
        this.spinnerTarget.classList.remove("hidden")
      } else {
        this.spinnerTarget.classList.add("hidden")
      }
    }
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = loading
    }
  }
}
